"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { Favicon } from "@/components/icons/favicon";
import { useScrollDirection } from "@/hooks/use-scroll-direction";
import type { SectionDef } from "@/lib/constants/sections";
import { Button } from "@domainstack/ui/button";
import { ScrollArea } from "@domainstack/ui/scroll-area";
import { cn } from "@domainstack/ui/utils";
interface SectionNavProps {
  domain: string;
  sections: SectionDef[];
  activeSection: string;
  isHeaderVisible: boolean;
  onSectionClick: (slug: string) => void;
}

/**
 * Sticky section navigation bar with "context injection" pattern.
 * When the page header scrolls out of view, the domain name and track button
 * fade into the left side of the nav bar.
 *
 * Desktop: Sticks below global header (always visible)
 * Mobile: Adjusts position based on global header visibility (scroll direction)
 */
export function SectionNav(t0) {
  const $ = _c(36);
  const {
    domain,
    sections,
    activeSection,
    isHeaderVisible,
    onSectionClick
  } = t0;
  const navRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      threshold: 15
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    direction,
    isPastThreshold
  } = useScrollDirection(t1);
  const isGlobalHeaderHidden = isPastThreshold && direction === "down";
  let t2;
  let t3;
  if ($[1] !== activeSection) {
    t2 = () => {
      const nav = navRef.current;
      if (!nav) {
        return;
      }
      const activeTab = nav.querySelector(`[data-section="${activeSection}"]`);
      if (!(activeTab instanceof HTMLElement)) {
        return;
      }
      activeTab.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center"
      });
    };
    t3 = [activeSection];
    $[1] = activeSection;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const t4 = isPastThreshold ? isGlobalHeaderHidden ? "top-0 transition-[top] duration-300 ease-out" : "top-[var(--header-height)] transition-[top] duration-300 ease-out" : "top-0";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("sticky z-40 -mx-4 mt-4 mb-4 px-4", t4, "md:!top-[var(--header-height)] md:!transition-none");
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = isHeaderVisible ? "after:bg-gradient-to-r after:from-transparent after:via-black/10 after:to-transparent dark:after:via-white/10" : "shadow-xl shadow-black/5 after:bg-black/10 dark:shadow-none dark:after:bg-white/10";
  let t7;
  if ($[6] !== t6) {
    t7 = cn("relative flex items-center", "h-[var(--section-nav-height)]", "before:absolute before:inset-y-0 before:left-1/2 before:-z-10 before:w-screen before:-translate-x-1/2", "before:bg-background/80 before:backdrop-blur", "after:absolute after:bottom-0 after:left-1/2 after:h-px after:w-screen after:-translate-x-1/2", "after:transition-opacity after:duration-200", t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = isHeaderVisible ? "w-0 opacity-0" : "mr-3 w-auto border-r border-black/10 pr-3 opacity-100 dark:border-white/10";
  let t9;
  if ($[8] !== t8) {
    t9 = cn("flex shrink-0 items-center gap-2 overflow-hidden transition duration-200 ease-out", t8);
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = `Scroll to top - ${domain}`;
  let t11;
  let t12;
  if ($[10] !== domain) {
    t11 = <Favicon domain={domain} className="shrink-0" />;
    t12 = <span className="max-w-32 truncate text-[15px] font-medium">{domain}</span>;
    $[10] = domain;
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10 || $[14] !== t11 || $[15] !== t12) {
    t13 = <button type="button" onClick={_temp} className="flex min-w-0 cursor-pointer items-center gap-2" aria-label={t10}>{t11}{t12}</button>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t13 || $[18] !== t9) {
    t14 = <div className={t9}>{t13}</div>;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== activeSection || $[21] !== onSectionClick || $[22] !== sections) {
    let t16;
    if ($[24] !== activeSection || $[25] !== onSectionClick) {
      t16 = t17 => {
        const {
          slug,
          title,
          icon: Icon,
          accent
        } = t17;
        return <Button key={slug} data-section={slug} data-accent={accent} variant="ghost" size="sm" onClick={() => onSectionClick(slug)} aria-current={activeSection === slug ? "page" : undefined} style={{
          "--section-accent": `var(--accent-${accent})`
        } as React.CSSProperties} className={cn("rounded-md px-3 py-1.5 text-[13px] tracking-[0.01em] whitespace-nowrap", "transition duration-150", "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none", "hover:!bg-[color-mix(in_oklch,var(--section-accent)_15%,transparent)] hover:text-foreground", activeSection === slug ? "bg-[color-mix(in_oklch,var(--section-accent)_25%,transparent)] font-medium text-accent-foreground" : "text-foreground/75")}><Icon className="size-3.5" />{title}</Button>;
      };
      $[24] = activeSection;
      $[25] = onSectionClick;
      $[26] = t16;
    } else {
      t16 = $[26];
    }
    t15 = sections.map(t16);
    $[20] = activeSection;
    $[21] = onSectionClick;
    $[22] = sections;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[27] !== t15) {
    t16 = <ScrollArea className="flex-1 [&_[data-slot=scroll-area-viewport]]:overscroll-y-none" hideScrollbar={true}><div className="flex items-center gap-1 px-1 md:justify-center">{t15}</div></ScrollArea>;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t14 || $[30] !== t16 || $[31] !== t7) {
    t17 = <div className={t7}>{t14}{t16}</div>;
    $[29] = t14;
    $[30] = t16;
    $[31] = t7;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17 || $[34] !== t5) {
    t18 = <nav ref={navRef} aria-label="Section navigation" className={t5}>{t17}</nav>;
    $[33] = t17;
    $[34] = t5;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
}
function _temp() {
  return window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
}